/**
 * Placeholder game palette + feel/tuning constants (CP21).
 *
 * `Team Knowledge/Guidelines/GL-003-design-system.md` is unpopulated (every
 * section still shows `<placeholder>` values) - per GL-003's own fallback
 * rule, this scaffold works in flagged fallback mode rather than inventing
 * brand values. Even once populated, GL-003 is a *web* design-system doc
 * (color roles, type scale, spacing) - it would not cover Phaser-canvas
 * fill colors, hit-timing, or "juice" constants, which are game-feel/art
 * decisions, not web tokens.
 *
 * This file exists so those values have exactly ONE home instead of being
 * scattered as magic numbers through scene files (the spirit of Felix's
 * "never hardcode colors/spacing" rule, adapted to a canvas game). Every
 * value below is a placeholder pending:
 *   1. Iris/Ruben sign-off on a real palette, and/or
 *   2. the §9 seed-locked stacked-prompt art pipeline replacing the
 *      placeholder LEGO-brick SVGs under `assets/sprites/` wholesale.
 * Do not treat any hex value here as a brand decision.
 */

/** Canvas fill colors (Phaser wants 0xRRGGBB numbers, not CSS hex strings). */
export const GAME_PALETTE = {
  hudBackground: 0x0b1f12,
  hudText: 0xeaf5ec,
  hudBarBeornings: 0xb45b3d,
  hudBarLothwen: 0x4f9d6f,
  hudBarTrack: 0x1a3320,
  promptText: 0xe0c97b,
  toastBackground: 0x08210f,
  toastText: 0xffd27f,
} as const;

/** Animation/juice timing (Brief §9 point 6 - "modest but satisfying"). */
export const GAME_TIMING = {
  /** Debounce between registered hits so key-repeat doesn't feel like a mash exploit. */
  chopHitFlashMs: 140,
  treeFallMs: 420,
  plantGrowMs: 500,
  toastDurationMs: 2200,
  cameraFollowLerp: 0.08,
  playerSpeedPxPerSec: 200,
} as const;

/** Difficulty-gradient tuning consumed by `world/difficulty.ts` - see that file's own caveat. */
export const GAME_TUNING = {
  baseHits: 1,
  distanceStepTiles: 3,
  hitsPerDistanceStep: 1,
  hrOverCeilingBonusHits: 2,
} as const;

/** World layout constants shared by the scene and the pure world/tile math. */
export const WORLD_LAYOUT = {
  /** World-space px between adjacent tree/plot slots on either side. */
  tileSpacingPx: 140,
  /** How many un-resolved tiles beyond the frontier render as "sensed" placeholders. */
  renderHorizonTiles: 12,
  groundY: 420,
  interactRadiusPx: 46,
} as const;
